/**
 * @module app.cm
 */

import { autoinject, TaskQueue, observable, BindingEngine, ICollectionObserverSplice, Disposable } from 'aurelia-framework';
import { ComponentBase, StandardLoadOptions, Sorting, SortDirection } from 'app/shared/lib/essette-site';
import { CaseService } from 'app/cm/services/case-service';
import { FaxService } from 'app/fm/services/fax-service';
import { DocumentsService } from 'app/core/services/documents-service';
import { MemberService, IMemberSearchFilters } from 'app/cd/services/member-service';
import { IDocumentsConfig } from 'app/core/models/essette-interfaces';
import { ManageCasesPageContext } from 'app/cm/lib/manage-cases-page-context';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { MemberSelect } from 'app/cd/components/member-select'
import { EssTableCustomElement, SelectEvent } from 'app/shared/elements/ess-table';
import { apiCall, ApiQueryResponse } from 'app/shared/lib/essette-api';
import { validate } from 'app/shared/elements/ess-rule';
import { getReference, getReferences } from 'app/shared/models/essette-types';
import { IApiSettings, IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { getApiSetting } from 'app/shared/lib/api/api-settings';
import { confirmation } from 'app/shared/elements/ess-confirm';
import { Toaster } from 'app/shared/lib/toaster';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { openCaseSummary } from 'app/cm/components/summary/case-summary';
import { guid } from 'app/shared/lib/guid';
import { downloadFile, openFileInWindow } from 'app/shared/lib/files';
import { I18N } from 'aurelia-i18n';
import { getDocumentIcon } from 'app/shared/lib/utilities';
import * as CM from 'app/cm/models/essette-api';
import * as FM from 'app/fm/models/essette-api';
import * as CD from 'app/cd/models/essette-api';
import * as Core from 'app/core/models/essette-api';
import { EventAggregator } from 'aurelia-event-aggregator';
import { StoreFocusEvent, ReturnFocusEvent, EnableReturnFocusEvent } from 'app/shared/lib/events';
interface ICaseCreateFields {
    assignTo: CM.CaseOwner | null;
    referralSource: CM.ReferralSource | null;
    referralDestination: CM.ReferralDestination | null;
    referralReason: CM.ReferralReason | null;
    referralNote?: string;
    programGroup: CM.ProgramGroup | null;
    program: CM.Program | null;
    acuity: CM.Acuity | null;
    intensity: CM.Intensity | null;
}

@autoinject()
export class CreateCasePage extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @observable
    public member: CD.Member | null = {};

    private get memberId(): string | null {
        return (this.member && this.member.id) || null;
    }

    protected caseSettings: IApiSettings = {};
    protected caseResults: CM.Case[] = [];
    private sorting = new Sorting();
    private resultCount: number = 0;
    protected pageSize: number = 5;
    protected criteriaTypes: CM.ReferralType[] = [];
    protected selectedCriteria: CM.ReferralCriteria[] = [];
    protected criteriaMet: boolean = false;
    protected referralMode: boolean = false;
    protected users: CM.CaseOwner[] = [];
    protected referralSources: CM.ReferralSource[] = [];
    protected referralDestinations: CM.ReferralDestination[] = [];
    protected referralReasons: CM.ReferralReason[] = [];
    protected referralOrigins: CM.ReferralOrigin[] = [];
    protected referralOrigin: CM.ReferralOrigin | null = null;
    protected programGroups: CM.ProgramGroup[] | null = null;
    protected acuities: CM.Acuity[] = [];
    protected intensities: CM.Intensity[] = [];
    public createFields: ICaseCreateFields = {
        assignTo: null,
        referralSource: null,
        referralDestination: null,
        referralReason: null,
        programGroup: null,
        program: null,
        acuity: null,
        intensity: null
    };
    protected selectedCriteriaTypes: CM.ReferralType[] = [];
    protected forceMemberSearch: boolean = false;
    protected subscription: Disposable | null = null;
    protected showAssignCase: boolean = false;
    protected acuityRequired: boolean = false;
    protected intensityRequired: boolean = false;
    protected programRequired: boolean = false;

    protected validate = validate;
    protected isValid: boolean = false;
    protected assignedToInvalid: boolean | null = null;

    public caseCreated: boolean = false;

    protected hasCaseOwner: boolean = false;
    protected hasCaseRoute: boolean = false;
    protected showCaseSummaryLink: boolean = false;
    protected openCaseSummary = openCaseSummary;

    //#region Fax Document Properties
    protected getDocumentIcon = getDocumentIcon;
    protected selectedCaseId: string | null = null;
    protected faxDocument: FM.FaxDocument | null = null;
    private faxDocumentId: string | null = null;
    protected documentsConfig: IDocumentsConfig = {
        serviceRootUrl: 'cm/cases',
        targetType: 'CM.Case',
        addTargetProperty: (document: any /*CM.CaseDocument*/) => { document.case = { id: this.selectedCaseId } }
    };
    //#endregion Fax Document Properties

    //#region Household Case Properties
    protected householdCaseId: string | null = null;
    protected householdCase: CM.HouseholdCase | null = null;
    protected assignedToLocked: boolean = false;
    protected programGroupLocked: boolean = false;
    protected programLocked: boolean = false;
    protected acuityLocked: boolean = false;
    protected intensityLocked: boolean = false;

    protected assessments: CM.ProgramCaseAssessment[] = [];
    protected tasks: CM.ProgramCaseTask[] = [];
    protected letters: CM.ProgramCaseCorrespondenceTemplate[] = [];
    protected goals: CM.ProgramCarePlanGoal[] = [];

    protected selectedAssessments: CM.ProgramCaseAssessment[] = [];
    protected selectedTasks: CM.ProgramCaseTask[] = [];
    protected selectedLetters: CM.ProgramCaseCorrespondenceTemplate[] = [];
    protected selectedGoals: CM.ProgramCarePlanGoal[] = [];
    //#endregion Household Case Properties

    ////****************** Element References ***************************************////
    protected $pager!: EssPagerCustomElement;
    protected memberSelect: MemberSelect | null = null;
    public $referralForm: HTMLElement | null = null;
    protected $assessmentsTable!: EssTableCustomElement<CM.ProgramCaseAssessment>;
    protected $tasksTable!: EssTableCustomElement<CM.ProgramCaseTask>;
    protected $lettersTable!: EssTableCustomElement<CM.ProgramCaseCorrespondenceTemplate>;
    protected $goalsTable!: EssTableCustomElement<CM.ProgramCarePlanGoal>;
    protected $collapsibleCreateCaseFor: HTMLElement | null = null;
    protected $collapsibleCriteriaForEntry: HTMLElement | null = null;

    protected id: guid = guid();

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly events: EventAggregator,
        private readonly bindingEngine: BindingEngine,
        private readonly caseService: CaseService,
        private readonly faxService: FaxService,
        private readonly memberService: MemberService,
        private readonly documentService: DocumentsService,
        private readonly context: ManageCasesPageContext,
        private readonly queue: TaskQueue,
        private readonly i18n: I18N,
        private readonly toaster: Toaster,
        ...rest: any[]) {

        super(...rest);

        this.subscription = this.bindingEngine.collectionObserver(this.selectedCriteria)
            .subscribe(this.selectedCriteriaChanged.bind(this));
    }

    protected async activate(params: { memberId: string, searchMode?: boolean, origin?: string, householdCaseId?: string, faxDocumentId: string }): Promise<void> {
        this.unlockFields();
        await this.checkPermissions();

        this.caseSettings = await this.loadSettings();
        this.acuityRequired = !!getApiSetting(this.caseSettings, 'acuity.!insert.$required');
        this.intensityRequired = !!getApiSetting(this.caseSettings, 'intensity.!insert.$required');
        this.programRequired = !!getApiSetting(this.caseSettings, 'program.!insert.$required');

        if(params.faxDocumentId) {
            this.faxDocumentId = params.faxDocumentId;
            this.documentService.configure(this.documentsConfig);
            await this.loadFaxDocument(this.faxDocumentId);
        }

        await this.loadLookups();

        if(params.memberId && params.memberId !== this.memberId) {
            this.member = await this.loadMember(params.memberId);
        } else {
            this.member = null;
        }

        if(params.searchMode) {
            this.forceMemberSearch = true;
        }

        if(params.origin) {
            this.referralOrigin = this.referralOrigins.find((o) => o.code === params.origin) || null;
            if(this.referralOrigin) {
                this.referralMode = !!this.referralOrigin.flag?.requiresVerification;
                this.showAssignCase = !!this.referralOrigin.flag?.assignsCase;
            }
            else {
                this.toaster.warn().message(`No origin found for ${params.origin}`).show();
            }
        } else {
            this.referralOrigin = null;
            this.showAssignCase = true;
        }

        this.householdCaseId = params.householdCaseId || null;
    }

    protected attached(): void {
        if(this.memberSelect) {
            if(this.forceMemberSearch) {
                this.memberSelect.search(true, true);
            }
            if(this.member) {
                this.memberSelect.selectMember(this.member);
            }
        }

        if(this.referralOrigin?.code === 'CM.HouseholdCase' && this.householdCaseId) {
            this.loadHouseholdCase();
        }
    }

    protected unbind() {
        if(this.subscription) {
            this.subscription.dispose();
        }
    }

    ////****************** Events ***************************************************////
    protected memberChanged(newMember: CD.Member | null, oldMember: CD.Member | null): void {
        this.refreshResults();
        this.criteriaMet = false;
        this.referralMode = false;
        this.assignedToInvalid = null;
    }

    protected assignedToChanged(): void {
        this.verifyAssignment();
    }

    protected memberSearchFiltersChanged(newFilters: IMemberSearchFilters, oldFilters: IMemberSearchFilters): void {
        this.context.memberSearchFilters = newFilters;
    }

    protected selectedCriteriaChanged(splices: ICollectionObserverSplice[]): void {
        this.updateCriteriaMet();
    }

    protected applySort(event: SortChangeEvent): void {
        const { property, direction } = event.detail;
        const { sorting } = this;

        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        this.refreshResults();
    }

    protected selectedProgramChanged(): void {
        if(!this.createFields.program) return;
        this.loadProgramDetails();
    }

    protected programAssessmentSelected(event: SelectEvent<CM.ProgramCaseAssessment>) {
        this.selectedAssessments = event.detail.items.map(item => <CM.ProgramCaseAssessment>getReference(item));
    }

    protected programTaskSelected(event: SelectEvent<CM.ProgramCaseTask>) {
        this.selectedTasks = event.detail.items.map(item => <CM.ProgramCaseTask>getReference(item));
    }

    protected programLetterSelected(event: SelectEvent<CM.CaseCorrespondenceTemplate>) {
        this.selectedLetters = event.detail.items.map(item => <CM.ProgramCaseCorrespondenceTemplate>getReference(item));
    }

    protected programGoalSelected(event: SelectEvent<CM.ProgramCarePlanGoal>) {
        this.selectedGoals = event.detail.items.map(item => <CM.ProgramCarePlanGoal>getReference(item));
    }

    protected handleDownloadFile(event: MouseEvent): void {
        if(this.faxDocument) {
            const docContentTypeLength = this.faxDocument?.document?.mediaType?.contentType?.length || 0
            if(event.shiftKey || event.ctrlKey || this.shouldView(this.faxDocument?.document?.mediaType?.contentType?.slice(docContentTypeLength - 3) || '')) {
                const childWindow = window.open("", "", 'top=25, left=' + (screen.width / 4) + ',width=1000,height=' + (screen.height - 150));
                if(childWindow) {
                    this.viewDocument(this.faxDocument, childWindow);
                    return;
                }
            }

            this.downloadDocument(this.faxDocument);
        }
    }

    ////****************** Functions ************************************************////
    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();
        this.hasCaseOwner = this.accountContext.hasPermission('CM.Case.Duty.Owner');
        this.hasCaseRoute = this.accountContext.hasRouteAccess('cm.case')
        this.showCaseSummaryLink = this.accountContext.hasRouteAccess('cm.case.summary');
    }

    private async verifyAssignment(): Promise<void> {
        const assignedToId = this.createFields?.assignTo?.id || '';
        if(!this.member || !this.member.id || !assignedToId) {
            this.assignedToInvalid = null;
            return;
        }

        try {
            this.state.beginProcessing();

            const response = await this.memberService.verifyMemberAccess(assignedToId, [{ id: this.member.id }]);

            if(this.createFields?.assignTo?.id !== assignedToId) {
                this.logger.info('Another Verification in Progress, Discarding Stale Results.');
            }
            else {
                this.assignedToInvalid = null;
                if(response.isSuccessful && response.result.filter && response.result.filter.items) {
                    this.assignedToInvalid = (response.result.filter.items.length === 0);
                }
            }
        }
        finally {
            this.state.endProcessing();
        }
    }

    private updateCriteriaMet() {
        this.criteriaMet = false;
        this.selectedCriteriaTypes = [];
        if(this.criteriaTypes.length > 0) {
            for(let type of this.criteriaTypes) {
                let minimumCriteria: number = type.requirements && type.requirements.minimumCriteria || 0;
                let criteriaCount: number = 0;
                for(let criteria of this.selectedCriteria) {
                    if(type.criteria && type.criteria.includes(criteria)) {
                        criteriaCount += 1;
                        if(!this.selectedCriteriaTypes.includes(type)) {
                            this.selectedCriteriaTypes.push(type);
                        }
                    }
                }
                if(criteriaCount >= minimumCriteria) {
                    this.criteriaMet = true;
                    break;
                }
            }
        }
        else { this.criteriaMet = true; }
    }

    protected continueReferral() {
        this.referralMode = true;
        this.queue.queueTask(() => {
            this.$collapsibleCreateCaseFor?.focus();
        });
        this.events.publish(new StoreFocusEvent());
    }

    protected navigateBack() {
        this.referralMode = false;
        this.events.publish(new EnableReturnFocusEvent());
        this.events.publish(new ReturnFocusEvent());
    }

    protected async createCase(openCase: boolean): Promise<void> {
        if(!this.isValid || this.caseCreated) return;

        const newCase: CM.Case = {
            member: getReference(this.member),
            assignedTo: getReference(this.createFields && this.createFields.assignTo),
            intensity: getReference(this.createFields.intensity),
            referral: {
                verification: {
                    criteria: getReferences(this.selectedCriteria)
                },
                source: getReference(this.createFields.referralSource),
                destination: getReference(this.createFields.referralDestination),
                origin: { code: this.referralOrigin?.code || 'ManageCases' },
                reason: getReference(this.createFields.referralReason),
                types: getReferences(this.selectedCriteriaTypes),
                comments: this.createFields.referralNote
            }
        }

        const householdCase: CM.HouseholdCase = {};

        if(!this.householdCase) {
            newCase.program = !!this.createFields.programGroup ? getReference(this.createFields.program) : undefined;
            newCase.acuity = getReference(this.createFields.acuity);
        } else {
            // build householdCase data for !initialize action
            newCase.householdCase = getReference(this.householdCase);
            if(this.createFields.program) {
                const program: CM.Program = this.createFields.program;
                program.assessments = [];
                program.tasks = [];
                program.letters = [];
                program.carePlan = {};
                program.carePlan.goals = [];

                if(this.selectedAssessments.length > 0) program.assessments.push(...this.selectedAssessments);
                if(this.selectedTasks.length > 0) program.tasks.push(...this.selectedTasks);
                if(this.selectedLetters.length > 0) program.letters.push(...this.selectedLetters);
                if(this.selectedGoals.length > 0) program.carePlan.goals.push(...this.selectedGoals);

                householdCase.id = newCase.householdCase?.id;
                householdCase.cases = [];
                householdCase.program = {
                    id: program.id,
                    assessments: program.assessments,
                    tasks: program.tasks,
                    letters: program.letters,
                    carePlan: {
                        goals: program.carePlan.goals
                    }
                };
            }
        }

        await this.insertCase(newCase, householdCase, openCase);
    }

    private unlockFields(): void {
        this.createFields = {
            assignTo: null,
            referralSource: null,
            referralDestination: null,
            referralReason: null,
            programGroup: null,
            program: null,
            acuity: null,
            intensity: null
        };
        this.assignedToLocked = false;
        this.programGroupLocked = false;
        this.programLocked = false;
        this.acuityLocked = false;
        this.intensityLocked = false;
    }

    /// Reset page data back to the beginning
    private async reset(): Promise<void> {
        if(this.faxDocument?.queue) {
            await this.checkinDocument();

            this.router.navigateToRoute('fm.queues.fax', { queueId: this.faxDocument.queue.id }, { replace: true, trigger: true });
        }
        else {
            this.member = null;
            this.householdCase = null;
            this.faxDocument = null;
            this.faxDocumentId = null;
            this.criteriaMet = false;
            this.referralMode = false;
            this.caseCreated = false;
            this.caseResults = [];
            this.selectedCriteria.splice(0, Infinity);  //Clear observable
            this.selectedCriteriaTypes = [];
            this.unlockFields();
        }
    }

    protected shouldView(extension: string): boolean {
        const allowed = ['pdf', 'jpg', 'jpeg', 'png', 'txt'];
        const extensionCheck = extension || '';
        return allowed.indexOf(extensionCheck) >= 0;
    }

    protected async downloadDocument(faxDocument: FM.FaxDocument): Promise<void> {
        if(!faxDocument?.id) { return; }

        try {
            const blob = await this.faxService.downloadFaxFile(faxDocument.id);
            const fileName = faxDocument.document?.file?.name || 'download';

            downloadFile(blob, fileName);
        }
        catch(error) {
            this.toaster.error().message(this.i18n.tr('ui:messages.errors.download', { what: this.i18n.tr('fm:faxDocument.$short') })).show();
        }
    }

    protected async viewDocument(faxDocument: FM.FaxDocument, childWindow: Window): Promise<void> {
        if(!faxDocument?.id) { return; }

        try {
            const blob = await this.faxService.downloadFaxFile(faxDocument.id);
            openFileInWindow(childWindow, blob);
        }
        catch(error) {
            this.toaster.error().message(this.i18n.tr('ui:messages.errors.download', { what: this.i18n.tr('fm:faxDocument.$short') })).show();
            childWindow.close();
        }
    }

    protected async openCase(selectedCase: CM.Case): Promise<void> {
        if(selectedCase && selectedCase.id) {
            this.selectedCaseId = selectedCase.id;

            // If Document is not null, attach the document to the case
            if(this.faxDocument?.document) {
                try {
                    if(!(this.documentsConfig && this.documentsConfig.addTargetProperty)) {
                        throw new Error("Documents configuration must define addTargetProperty.")
                    }

                    const result = await confirmation.showCustom(
                        this.i18n.tr('ui:actions.attachTo', { what: this.i18n.tr('core:document.$'), whom: this.i18n.tr('cm:case.$') }),
                        this.i18n.tr('cm:local.createCase.existingCaseAttachDocumentConfirmation', { what: this.faxDocument.document.$display, whom: selectedCase.number }),
                        confirmation.options.StandardConfirm
                    );

                    // Do not proceed with attachment
                    if(result.dialogResponse !== confirmation.options.OK) { return; }

                    //Attach Document to Case and Open Case Page directly to Documents tab
                    const attachDocument: CM.CaseDocument = {
                        case: { id: this.selectedCaseId },
                        id: this.faxDocument.document.id
                    };

                    if(this.documentsConfig && this.documentsConfig.addTargetProperty) {
                        this.documentsConfig.addTargetProperty(attachDocument);
                    }

                    await this.attachDocument(attachDocument);

                    this.router.navigate(`#/cm/case/${selectedCase.id}/documents/?queueTypeCode=FM.Document&queueId=${this.faxDocument?.queue?.id}&queueTargetId=${this.faxDocument?.document?.id}`);
                }
                catch(error) {
                    this.toaster.standard.saved('core:document.$', false);
                    this.logger.error('Error Attaching Document', error);
                }
            }
            else {
                this.router.navigateToRoute('cm.case', { 'caseId': selectedCase.id });
            }
        }
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadLookups(): Promise<void> {
        try {
            this.state.beginLoad();
            const options: StandardLoadOptions = { all: true };
            const [users, types, sources, destinations, reasons, origins, programGroups, acuities, intensities] = await Promise.all(
                [
                    this.caseService.loadCaseOwners(options),
                    this.loadReferralTypes(),
                    this.caseService.loadReferralSources(options),
                    this.caseService.loadReferralDestinations(),
                    this.caseService.loadReferralReasons(options),
                    this.caseService.loadReferralOrigins(),
                    this.caseService.loadProgramGroups(options, ['programs'], false, false, true),
                    this.caseService.loadAcuities(options),
                    this.caseService.loadIntensities(options)
                ]
            );

            this.users = users.results;
            this.criteriaTypes = types;
            this.referralSources = sources.results;
            this.referralDestinations = destinations.results;
            this.referralReasons = reasons.results;
            this.referralOrigins = origins.results;
            this.programGroups = programGroups.results;
            this.acuities = acuities.results;
            this.intensities = intensities.results;

            if(this.faxDocument?.queue?.actions?.some(a => a.code == 'CM.Case.Attach')) {
                const foundParameter = this.faxDocument.queue?.parameters?.find(p => p.description == 'CM Referred By');
                if(foundParameter) {
                    const referralSourceId = foundParameter.argument;
                    this.createFields.referralSource = this.referralSources.find(rs => rs.id == referralSourceId) || null;
                }
            }
        }
        catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadSettings(): Promise<IApiSettings> {
        try {
            this.state.beginLoad();

            const response = await this.caseService.loadCaseSettings();
            return response.settings;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async refreshResults(): Promise<void> {
        const options: StandardLoadOptions = {
            sorting: this.sorting,
            top: this.pageSize,
            page: this.$pager?.page || 0,
            includeInactive: false
        };

        try {
            this.state.beginLoad();
            const [cases, referralTypes] = await Promise.all(
                [
                    this.searchCases(options),
                    this.loadReferralTypes()
                ]
            );

            if(cases) {
                this.caseResults = cases.results;
                this.resultCount = cases.count || 0;
            }

            this.criteriaTypes = referralTypes || [];
            this.updateCriteriaMet();
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async searchCases(options?: StandardLoadOptions): Promise<ApiQueryResponse<CM.Case> | null> {
        if(!this.member || !this.member.id) return null;
        try {
            this.state.beginLoad();
            const includes: string[] = ['assignedTo', 'program', 'status'];
            const memberSearchFilters: IMemberSearchFilters = { id: this.member.id };

            const response = await this.caseService.searchCases({}, memberSearchFilters, options, includes);
            return response;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadReferralTypes(): Promise<CM.ReferralType[]> {
        const options: Partial<IQueryLoadOptions> = {
            all: true
        };
        const includes: string[] = ['criteria'];

        try {
            this.state.beginLoad();
            const response = await this.caseService.loadCriteria(options, includes);
            return response.results;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadMember(memberId: string): Promise<CM.Member> {
        try {
            this.state.beginLoad();
            const includes: string[] = [
                'primaryPlan',
                'primaryPlan.healthPlan',
                'primaryPlan.lineOfBusiness',
                'primaryCarePhysician'
            ];
            const response = await this.memberService.loadMember(memberId, includes);
            return response.results[0];
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadHouseholdCase(): Promise<void> {
        if(!this.householdCaseId) return;

        const includes: string[] = [
            'household',
            'program'
        ];

        try {
            this.state.beginLoad();

            const response = await this.caseService.loadHouseholdCase(this.householdCaseId, includes);
            if(!response.isSuccessful || response.results.length === 0) return;

            const householdCase: CM.HouseholdCase = response.results[0];
            this.householdCase = householdCase;

            const assignedTo: Core.User | undefined = this.users.find(a => a.id === householdCase.assignedTo?.id);
            if(assignedTo) {
                this.createFields.assignTo = assignedTo;
                this.assignedToLocked = true;
            }

            const acuity: CM.Acuity | undefined = this.acuities.find(a => a.id === householdCase.acuity?.id);
            if(acuity) {
                this.createFields.acuity = acuity;
                this.acuityLocked = true;
            }

            const intensity: CM.Intensity | undefined = this.intensities.find(i => i.id === householdCase.intensity?.id);
            if(intensity) {
                this.createFields.intensity = intensity;
                this.intensityLocked = true;
            }

            const programGroup: CM.ProgramGroup | undefined = this.programGroups?.find(pg => pg.id === householdCase.program?.group?.id);
            if(programGroup) {
                this.createFields.programGroup = programGroup;
                this.programGroupLocked = true;
                const program: CM.Program | undefined = programGroup?.programs?.find(p => p.id === householdCase.program?.id);
                if(program) {
                    this.createFields.program = program;
                    this.programLocked = true;
                    this.loadProgramDetails();
                }
            }
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadProgramDetails(): Promise<void> {
        if(!this.createFields.program?.id) return;

        const options: StandardLoadOptions = { includeInactive: false };

        const includes: string[] = [
            'assessments.assessment',
            'tasks.definition',
            'letters.template',
            'letters.acuities',
            'careplan.goals.definition',
            'careplan.goals.acuities',
        ];

        try {
            this.state.beginLoad();

            const response = await this.caseService.loadProgram(this.createFields.program.id, options, includes);;
            if(!response.isSuccessful || response.results.length === 0) return;

            const program: CM.Program = response.results[0];
            this.assessments = program.assessments ?? [];
            this.tasks = program.tasks ?? [];
            // filter letters and goals based on selected acuity
            this.letters = program.letters?.filter(letter => {
                return letter.acuities?.some(acuity => acuity.id === this.createFields.acuity?.id)
            }) ?? [];
            this.goals = program.carePlan?.goals?.filter(goal => {
                return goal.acuities?.some(acuity => acuity.id === this.createFields.acuity?.id)
            }) ?? [];

            // select all program details (after table data is bound)
            this.queue.queueTask(() => {
                this.$assessmentsTable.selectAll();
                this.$tasksTable.selectAll();
                this.$lettersTable.selectAll();
                this.$goalsTable.selectAll();
            });
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadFaxDocument(faxDocumentId: string): Promise<void> {
        try {
            this.state.beginLoad();
            const includes: string[] = [
                'document',
                'queue',
                'queue.actions',
                'queue.parameters',
                'priority'
            ];
            const response = await this.faxService.loadFaxDocument(faxDocumentId, includes);
            this.faxDocument = response.results[0];
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    @apiCall
    public async checkinDocument(): Promise<void> {
        if(!this.faxDocumentId) { return; }

        try {
            this.state.beginLoad();
            await this.faxService.checkInFaxDocument(this.faxDocumentId);
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async attachDocument(document: CM.CaseDocument): Promise<void> {
        try {
            this.state.beginSave();
            await this.documentService.attachDocument(document);
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endSave();
        }
    }

    @apiCall
    private async insertCase(newCase: CM.Case, householdCase: CM.HouseholdCase, openCase: boolean): Promise<void> {
        try {
            this.state.beginSave();

            const response = await this.caseService.insertCase(newCase);
            this.caseCreated = response.isSuccessful;

            if(!response.isSuccessful || !response.id) {
                this.toaster.standard.created('cm:case.$', false);
                return;
            } else if(householdCase.id) {
                // if insert is successful and HH case specified, followup with !initialize action to apply program data to newly-created case
                householdCase.cases!.push({ id: response.id });
                await this.initializeHouseholdCase(householdCase);
            }

            // If faxDocument is not null, prepare document to attach to the new Case
            if(this.faxDocument?.document) {
                const attachDocument: CM.CaseDocument = {
                    case: { id: response.id },
                    id: this.faxDocument.document.id
                };

                await this.attachDocument(attachDocument);
            }

            this.toaster.standard.created('cm:case.$', true);

            if(openCase) {
                if(this.hasCaseRoute) {
                    if(this.faxDocument) {
                        this.router.navigate(`#/cm/case/${response.id}/documents/?queueTypeCode=FM.Document&queueId=${this.faxDocument?.queue?.id}&queueTargetId=${this.faxDocument?.document?.id}`);
                    } else {
                        this.router.navigateToRoute('cm.case', { caseId: response.id });
                    }
                } else {
                    this.reset();
                    this.router.navigateToRoute('cm.manage-cases.create');
                }
            } else {
                if(this.referralOrigin && !this.faxDocument) {
                    this.router.navigateBack();
                } else {
                    this.reset();
                    this.router.navigateToRoute('cm.manage-cases.create');
                }
            }
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endSave();
        }
    }

    @apiCall
    private async initializeHouseholdCase(householdCase: CM.HouseholdCase): Promise<void> {
        try {
            this.state.beginSave();

            await this.caseService.initializeHouseholdCase(householdCase);
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endSave();
        }
    }
}

